// Instanzgebunden: keine Datenübernahme zwischen Benutzern oder Kalendern.
export function createCalendarReferenceCache({ maxAge = 60000, maxEntries = 24, now = Date.now } = {}) {
  const entries = new Map()
  return {
    get(key, load) {
      const existing = entries.get(key)
      if (existing && (existing.pending || now() - existing.loadedAt < maxAge)) return existing.promise
      const entry = { pending: true, loadedAt: 0, promise: null }
      entries.delete(key)
      entries.set(key, entry)
      while (entries.size > maxEntries) entries.delete(entries.keys().next().value)
      entry.promise = Promise.resolve().then(load).then((value) => {
        entry.pending = false
        entry.loadedAt = now()
        return value
      }, (error) => {
        // Fehler und inzwischen ungültige Einträge niemals als leere Daten cachen.
        if (entries.get(key) === entry) entries.delete(key)
        throw error
      })
      return entry.promise
    },
    invalidate() {
      entries.clear()
    },
  }
}

// Ein Burst wird zusammengefasst; Änderungen während eines Abrufs bekommen
// einen Folgeabruf. Alle Aufrufer erhalten weiterhin ein awaitbares Ergebnis.
export function createCalendarRefreshQueue(load, { delay = 180 } = {}) {
  let pending = []
  let running = null
  let timer = null
  let dueAt = 0
  let disposed = false
  const schedule = () => {
    clearTimeout(timer)
    timer = setTimeout(flush, Math.max(0, dueAt - Date.now()))
  }
  const flush = async () => {
    timer = null
    if (disposed || running || !pending.length) return
    const batch = pending
    pending = []
    running = batch
    const latest = batch[batch.length - 1].context
    const context = { ...latest, silent: batch.every((item) => item.context.silent) }
    let result = false
    try {
      result = await load(context)
    } catch (_) {
      // Der Kalender zeigt seinen Ladefehler; wartende Aktionen bleiben nicht hängen.
    } finally {
      batch.forEach((item) => item.resolve(!disposed && item.context.key === context.key ? result : false))
      running = null
      if (!disposed && pending.length) schedule()
    }
  }
  return {
    request(context) {
      if (disposed) return Promise.resolve(false)
      if (running?.some((item) => item.context.key === context.key && !item.context.silent)) {
        context = { ...context, silent: false }
      }
      const promise = new Promise((resolve) => pending.push({ context, resolve }))
      dueAt = Date.now() + delay
      schedule()
      return promise
    },
    dispose() {
      disposed = true
      clearTimeout(timer)
      ;[...pending, ...(running || [])].forEach((item) => item.resolve(false))
      pending = []
    },
  }
}
